<template>
  <div class="news-root">
    <!-- ── Top bar ─────────────────────────────────────────────────────── -->
    <header class="topbar">
      <div class="brand" @click="$router.push('/ceo-dashboard')">
        <button class="back-btn" title="Back to dashboard"><v-icon size="20" color="#14663a">mdi-arrow-left</v-icon></button>
        <div class="brand-logo"><img :src="wtiLogo" alt="WTI" /></div>
        <div class="brand-text">
          <div class="brand-title">External Media Coverage</div>
          <div class="brand-sub">Wildlife Trust of India · third-party news</div>
        </div>
      </div>
      <div class="topbar-actions">
        <button class="btn ghost" :disabled="scraping" @click="triggerScraper">
          <v-icon size="17" :class="{ spin: scraping }">mdi-web-sync</v-icon>
          <span>{{ scraping ? 'Scanning…' : 'Refresh News' }}</span>
        </button>
        <button class="btn solid" @click="openAddDialog">
          <v-icon size="17">mdi-plus</v-icon><span>Add Link</span>
        </button>
      </div>
    </header>

    <div v-if="loading" class="loader">
      <v-progress-circular indeterminate color="#14663a" size="46" width="4" />
      <div>Loading media coverage…</div>
    </div>

    <main v-else class="body">
      <!-- ── Auto coverage summary ─────────────────────────────────────── -->
      <div class="summary-banner" v-if="coverageSummary.length">
        <v-icon size="18" color="#14663a">mdi-text-box-check-outline</v-icon>
        <div class="sb-lines"><span v-for="(l, i) in coverageSummary" :key="i">{{ l }}</span></div>
      </div>

      <!-- ── Sentiment summary ─────────────────────────────────────────── -->
      <section class="sum-grid">
        <div class="sum-card">
          <div class="sum-head"><v-icon size="18" color="#14663a">mdi-newspaper-variant-multiple-outline</v-icon> Total Coverage</div>
          <div class="sum-val">{{ stats.total }}</div>
          <div class="sum-sub">Articles tracked</div>
        </div>
        <div class="sum-card">
          <div class="sum-head"><v-icon size="18" color="#2e7d32">mdi-emoticon-happy-outline</v-icon> Positive</div>
          <div class="sum-val pos">{{ stats.positive }} <span class="sum-pct">· {{ stats.positivePct }}%</span></div>
          <div class="sum-sub">Successes &amp; wins</div>
        </div>
        <div class="sum-card">
          <div class="sum-head"><v-icon size="18" color="#c62828">mdi-emoticon-sad-outline</v-icon> Negative</div>
          <div class="sum-val neg">{{ stats.negative }}</div>
          <div class="sum-sub">Alerts &amp; critical issues</div>
        </div>
        <div class="sum-card">
          <div class="sum-head"><v-icon size="18" color="#64748b">mdi-information-outline</v-icon> Neutral</div>
          <div class="sum-val neu">{{ stats.neutral }}</div>
          <div class="sum-sub">Notices &amp; general press</div>
        </div>
      </section>

      <!-- ── Media impact (authority-weighted) ─────────────────────────── -->
      <section class="impact" v-if="mediaImpact && mediaImpact.weightedImpact">
        <div class="impact-head">
          <span><v-icon size="18" color="#14663a">mdi-bullseye-arrow</v-icon> Media Impact <span class="muted">· weighted by channel authority (SEO rating)</span></span>
          <span class="momentum" v-if="momentum && momentum.last30 != null" :class="momentum.deltaPct >= 0 ? 'up' : 'down'">
            <v-icon size="15" :color="momentum.deltaPct >= 0 ? '#2e7d32' : '#c62828'">{{ momentum.deltaPct >= 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}</v-icon>
            {{ momentum.last30 }} articles / 30d ({{ momentum.deltaPct >= 0 ? '+' : '' }}{{ momentum.deltaPct }}% vs prev) · net sentiment {{ momentum.netSentiment >= 0 ? '+' : '' }}{{ momentum.netSentiment }}
          </span>
        </div>
        <div class="impact-tiles">
          <div class="imp emv" v-if="mediaImpact.estimatedValue"><div class="imp-v">{{ formatMoney(mediaImpact.estimatedValue) }}</div><div class="imp-l">Est. earned-media value (approx)</div></div>
          <div class="imp"><div class="imp-v">{{ mediaImpact.weightedImpact }}</div><div class="imp-l">Weighted reach · Σ authority</div></div>
          <div class="imp"><div class="imp-v">{{ mediaImpact.avgAuthority }}</div><div class="imp-l">Avg channel authority</div></div>
          <div class="imp"><div class="imp-v">{{ mediaImpact.tier1Pct }}%</div><div class="imp-l">In Tier-1 national outlets</div></div>
          <div class="imp risk" :class="riskClass(mediaImpact.reputationRisk)"><div class="imp-v">{{ mediaImpact.reputationRisk }}%</div><div class="imp-l">Reputation risk · weighted negative</div></div>
        </div>
      </section>

      <!-- ── Coverage reach: languages + sources ───────────────────────── -->
      <section class="reach" v-if="languageSpread.length > 1 || sourceSpread.length">
        <div class="reach-block" v-if="languageSpread.length">
          <div class="reach-label"><v-icon size="16" color="#14663a">mdi-translate</v-icon> Coverage by language</div>
          <div class="reach-chips">
            <span class="reach-chip" v-for="l in languageSpread" :key="l.code" :class="{ prime: l.code === 'en' }" :title="l.count + ' articles in ' + l.name">
              {{ l.name }} <b>{{ l.count }}</b>
            </span>
          </div>
        </div>
        <div class="reach-block" v-if="sourceSpread.length">
          <div class="reach-label"><v-icon size="16" color="#14663a">mdi-database-search-outline</v-icon> Sources</div>
          <div class="reach-chips">
            <span class="reach-chip src" v-for="s in sourceSpread" :key="s.source" :title="s.count + ' articles via ' + s.source">
              {{ s.source }} <b>{{ s.count }}</b>
            </span>
          </div>
        </div>
      </section>

      <!-- ── Trend + top publishers ────────────────────────────────────── -->
      <section class="insight-row" v-if="hasTrend || topPublishers.length">
        <div class="panel" v-if="hasTrend">
          <div class="panel-title"><v-icon size="18" color="#14663a">mdi-chart-timeline-variant</v-icon> Coverage &amp; Sentiment · last 10 weeks</div>
          <apexchart type="bar" height="240" :options="trendChart.options" :series="trendChart.series" />
        </div>
        <div class="panel" v-if="topPublishers.length">
          <div class="panel-title"><v-icon size="18" color="#14663a">mdi-domain</v-icon> Top Publishers <span class="muted">· articles · authority · sentiment</span></div>
          <div class="pub-head"><span class="ph-count">Articles</span><span class="ph-name">Publisher</span><span class="ph-da">Authority</span><span class="ph-sent">+ / −</span></div>
          <div class="pub-list">
            <div class="pub-row clickable" v-for="p in topPublishers" :key="p.publisher" @click="filterBy(p.publisher)" title="Click to filter articles by this publisher">
              <span class="pub-count" title="Articles about WTI from this publisher">{{ p.count }}</span>
              <span class="pub-name">{{ p.publisher }}</span>
              <span class="da-badge" :class="tierClass(p.tier)" :title="'Channel authority (SEO rating) ~' + p.authority + ' · Tier ' + p.tier">DA {{ p.authority }}</span>
              <span class="pub-sent">
                <span class="chip-pos" v-if="p.positive" :title="p.positive + ' positive articles'"><v-icon size="11" color="#2e7d32">mdi-thumb-up</v-icon> {{ p.positive }}</span>
                <span class="chip-neg" v-if="p.negative" :title="p.negative + ' negative articles'"><v-icon size="11" color="#c62828">mdi-thumb-down</v-icon> {{ p.negative }}</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      <!-- ── Share of Voice vs peer NGOs ───────────────────────────────── -->
      <section class="panel sov-panel" v-if="sovBars.length">
        <div class="panel-title"><v-icon size="18" color="#14663a">mdi-podium-gold</v-icon> Share of Voice · WTI vs peer NGOs <span class="muted">· recent Google News coverage</span></div>
        <div class="sov-list">
          <div class="sov-row" v-for="o in sovBars" :key="o.org" :class="{ self: o.self }">
            <span class="sov-name">{{ o.org }}<span class="sov-you" v-if="o.self">you</span></span>
            <span class="sov-track"><span class="sov-fill" :class="{ me: o.self }" :style="{ width: o.pct + '%' }"></span></span>
            <span class="sov-count">{{ o.count }} <em>· {{ o.sharePct }}%</em></span>
          </div>
        </div>
      </section>

      <!-- ── Media ↔ Fundraising correlation ───────────────────────────── -->
      <section class="panel corr-panel" v-if="corr && corr.series && corr.series.length">
        <div class="panel-title">
          <v-icon size="18" color="#14663a">mdi-chart-bell-curve-cumulative</v-icon>
          Media coverage vs Online giving <span class="muted">· {{ corr.fy }}</span>
        </div>
        <div class="corr-lead" :class="corrClass">
          <span class="corr-stat">
            <b v-if="corr.correlation !== null">r = {{ corr.correlation }}</b>
            <b v-else>—</b>
          </span>
          <span class="corr-word">{{ corr.strength }} link</span>
          <span class="corr-note">{{ corr.note }}</span>
        </div>
        <apexchart type="line" height="260" :options="corrChart.options" :series="corrChart.series" />
      </section>

      <!-- ── Themes + reputation / wins ────────────────────────────────── -->
      <section class="insight-row wide" v-if="themes.length || reputationWatch.length || topWins.length">
        <div class="panel" v-if="themes.length">
          <div class="panel-title"><v-icon size="18" color="#14663a">mdi-shape-outline</v-icon> Coverage by Theme</div>
          <div class="theme-list">
            <div class="theme-row" v-for="t in themeBars" :key="t.theme">
              <span class="theme-name">{{ t.theme }}</span>
              <span class="theme-track"><span class="theme-fill" :style="{ width: t.pct + '%', background: t.color }"></span></span>
              <span class="theme-count">{{ t.count }}</span>
            </div>
          </div>
          <div class="species-chips" v-if="species.length">
            <span class="sp-label">Species in the news:</span>
            <button class="sp-chip" v-for="s in species" :key="s.name" @click="filterBy(s.name)" title="Filter articles">{{ s.name }} <b>{{ s.count }}</b></button>
          </div>
        </div>

        <div class="panel" v-if="reputationWatch.length || topWins.length">
          <div class="panel-title"><v-icon size="18" color="#c62828">mdi-shield-alert-outline</v-icon> Reputation Watch</div>
          <div class="watch-list">
            <a class="watch-row neg" v-for="(a, i) in reputationWatch" :key="'n' + i" :href="a.url" target="_blank" rel="noopener noreferrer">
              <span class="watch-da" :class="tierClass(a.tier)">{{ a.authority }}</span>
              <span class="watch-title">{{ a.title }}</span>
              <span class="watch-pub">{{ a.publisher }}</span>
            </a>
            <div class="watch-empty" v-if="!reputationWatch.length"><v-icon size="18" color="#2e7d32">mdi-check-circle-outline</v-icon> No negative coverage flagged.</div>
          </div>
          <div class="panel-title sub" v-if="topWins.length"><v-icon size="16" color="#2e7d32">mdi-trophy-outline</v-icon> Recent Wins</div>
          <div class="watch-list" v-if="topWins.length">
            <a class="watch-row pos" v-for="(a, i) in topWins.slice(0, 3)" :key="'p' + i" :href="a.url" target="_blank" rel="noopener noreferrer">
              <span class="watch-da" :class="tierClass(a.tier)">{{ a.authority }}</span>
              <span class="watch-title">{{ a.title }}</span>
              <span class="watch-pub">{{ a.publisher }}</span>
            </a>
          </div>
        </div>
      </section>

      <!-- ── Filters ───────────────────────────────────────────────────── -->
      <section class="filters">
        <div class="search">
          <v-icon size="18" color="#8a978f">mdi-magnify</v-icon>
          <input type="text" v-model="searchQuery" placeholder="Search headline, publisher or author…" @input="debouncedFetch" />
          <button v-if="searchQuery" class="clear" @click="searchQuery = ''; fetchNews()"><v-icon size="15" color="#8a978f">mdi-close</v-icon></button>
        </div>
        <div class="chips">
          <button v-for="d in dateChips" :key="d.v" class="chip" :class="{ on: dateFilter === d.v }" @click="setDateFilter(d.v)">{{ d.t }}</button>
        </div>
        <div class="chips">
          <button class="chip" :class="{ on: selectedSentiment === 'all' }" @click="setSentimentFilter('all')">All</button>
          <button class="chip pos" :class="{ on: selectedSentiment === 'positive' }" @click="setSentimentFilter('positive')">Positive</button>
          <button class="chip neg" :class="{ on: selectedSentiment === 'negative' }" @click="setSentimentFilter('negative')">Negative</button>
          <button class="chip neu" :class="{ on: selectedSentiment === 'neutral' }" @click="setSentimentFilter('neutral')">Neutral</button>
        </div>
        <div class="chips" v-if="languageSpread.length > 1">
          <button class="chip lang" :class="{ on: selectedLanguage === 'all' }" @click="setLanguageFilter('all')">All languages</button>
          <button v-for="l in languageSpread" :key="l.code" class="chip lang" :class="{ on: selectedLanguage === l.code }" @click="setLanguageFilter(l.code)">{{ l.name }} <b>{{ l.count }}</b></button>
        </div>
      </section>

      <div class="result-note" v-if="items.length">{{ pagination.totalItems }} article{{ pagination.totalItems === 1 ? '' : 's' }}</div>

      <!-- ── Articles ──────────────────────────────────────────────────── -->
      <section v-if="items.length" class="grid">
        <article v-for="item in items" :key="item._id" class="card" :class="item.sentiment">
          <div class="card-thumb" v-if="item.imageUrl" :class="{ fav: isFavicon(item.imageUrl) }">
            <img :src="item.imageUrl" @error="imgError" alt="" />
          </div>
          <div class="card-top">
            <span class="pub">
              <v-icon size="14" color="#14663a">mdi-newspaper</v-icon> {{ item.publisher || 'External Media' }}
              <span class="da-badge sm" :class="tierClass(item.tier)" v-if="item.authority" :title="'Authority ~' + item.authority + ' · Tier ' + item.tier">DA {{ item.authority }}</span>
            </span>
            <span class="badge" :class="item.sentiment">
              <v-icon size="12" :color="sentColor(item.sentiment)">{{ sentimentIcon(item.sentiment) }}</v-icon> {{ item.sentiment }}
            </span>
          </div>
          <a class="card-title" :href="item.articleUrl" target="_blank" rel="noopener noreferrer">{{ item.title }}</a>
          <p class="card-snippet" v-if="item.snippet">{{ item.snippet }}</p>
          <div class="card-foot">
            <span class="date"><v-icon size="13" color="#a7b3ac">mdi-clock-outline</v-icon> {{ formatDate(item.publishedAt) }}</span>
            <div class="foot-actions">
              <v-menu offset-y left>
                <template v-slot:activator="{ on, attrs }">
                  <button class="mini-btn" v-bind="attrs" v-on="on"><v-icon size="14" color="#64748b">mdi-tag-outline</v-icon> Sentiment</button>
                </template>
                <v-list dense>
                  <v-list-item @click="updateSentiment(item, 'positive')"><v-list-item-title>✅ Positive</v-list-item-title></v-list-item>
                  <v-list-item @click="updateSentiment(item, 'negative')"><v-list-item-title>⛔ Negative</v-list-item-title></v-list-item>
                  <v-list-item @click="updateSentiment(item, 'neutral')"><v-list-item-title>➖ Neutral</v-list-item-title></v-list-item>
                </v-list>
              </v-menu>
              <a class="read" :href="item.articleUrl" target="_blank" rel="noopener noreferrer">Read <v-icon size="13">mdi-arrow-top-right</v-icon></a>
            </div>
          </div>
        </article>
      </section>

      <div v-else class="empty">
        <v-icon size="42" color="#c9d6ce">mdi-newspaper-remove</v-icon>
        <h3>No media coverage found</h3>
        <p>Try widening the date range or clearing filters.</p>
        <button class="btn ghost" @click="resetFilters">Reset filters</button>
      </div>

      <div class="pager" v-if="pagination.totalPages > 1">
        <button class="btn ghost" :disabled="pagination.page <= 1" @click="changePage(pagination.page - 1)"><v-icon size="16">mdi-chevron-left</v-icon> Prev</button>
        <span>Page {{ pagination.page }} of {{ pagination.totalPages }}</span>
        <button class="btn ghost" :disabled="pagination.page >= pagination.totalPages" @click="changePage(pagination.page + 1)">Next <v-icon size="16">mdi-chevron-right</v-icon></button>
      </div>
    </main>

    <!-- ── Add dialog ──────────────────────────────────────────────────── -->
    <v-dialog v-model="addDialog" max-width="540" persistent>
      <v-card class="add-card">
        <div class="add-title">Add a Media Article <v-btn icon small @click="addDialog = false"><v-icon>mdi-close</v-icon></v-btn></div>
        <div class="add-body">
          <v-text-field v-model="newArticle.title" label="Headline *" outlined dense hide-details class="mb-3" />
          <v-text-field v-model="newArticle.articleUrl" label="Article URL *" outlined dense hide-details class="mb-3" />
          <div class="add-row">
            <v-text-field v-model="newArticle.publisher" label="Publisher" outlined dense hide-details placeholder="e.g. The Hindu" />
            <v-select v-model="newArticle.sentiment" :items="sentimentOptions" item-text="text" item-value="value" label="Sentiment" outlined dense hide-details />
          </div>
          <v-textarea v-model="newArticle.snippet" label="Summary (optional)" outlined dense hide-details rows="2" class="mt-3" />
        </div>
        <div class="add-actions">
          <button class="btn ghost" @click="addDialog = false">Cancel</button>
          <button class="btn solid" :disabled="savingArticle" @click="submitAddArticle">{{ savingArticle ? 'Saving…' : 'Add Article' }}</button>
        </div>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackColor" timeout="3000" bottom right>{{ snackMsg }}</v-snackbar>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
  name: 'CeoMediaNews',
  data() {
    return {
      wtiLogo: require('@/assets/images/wtilogo.png'),

      loading: true,
      scraping: false,
      savingArticle: false,
      addDialog: false,
      searchQuery: '',
      dateFilter: 'all',
      selectedSentiment: 'all',
      selectedLanguage: 'all',
      items: [],
      stats: { total: 0, positive: 0, negative: 0, neutral: 0, positivePct: 0 },
      trend: [],
      topPublishers: [],
      mediaImpact: {},
      themes: [],
      species: [],
      reputationWatch: [],
      topWins: [],
      momentum: {},
      languageSpread: [],
      sourceSpread: [],
      corr: null,
      shareOfVoice: { orgs: [], wtiShare: 0, wtiRank: 0 },
      pagination: { page: 1, limit: 12, totalItems: 0, totalPages: 1 },
      newArticle: { title: '', articleUrl: '', publisher: '', author: '', sentiment: 'positive', snippet: '' },
      sentimentOptions: [
        { text: 'Positive', value: 'positive' },
        { text: 'Negative', value: 'negative' },
        { text: 'Neutral', value: 'neutral' }
      ],
      dateChips: [
        { v: 'all', t: 'All time' },
        { v: '30days', t: 'Last 30 days' },
        { v: '7days', t: 'Last 7 days' },
        { v: 'yesterday', t: 'Since yesterday' }
      ],
      snackbar: false,
      snackMsg: '',
      snackColor: '#14663a',
      debounceTimer: null
    };
  },
  computed: {
    hasTrend() { return (this.trend || []).some((t) => t.total > 0); },
    themeBars() {
      const t = this.themes || [];
      const max = Math.max(1, ...t.map((x) => x.count));
      return t.map((x) => ({
        ...x,
        pct: Math.max(5, Math.round((x.count / max) * 100)),
        color: /controvers|crime/i.test(x.theme) ? '#c62828' : (/award|rescue/i.test(x.theme) ? '#2e7d32' : '#14663a'),
      }));
    },
    sovBars() {
      const o = (this.shareOfVoice && this.shareOfVoice.orgs) || [];
      const max = Math.max(1, ...o.map((x) => x.count));
      return o.map((x) => ({ ...x, pct: Math.max(4, Math.round((x.count / max) * 100)) }));
    },
    coverageSummary() {
      const out = [], s = this.stats, m = this.momentum, mi = this.mediaImpact, sov = this.shareOfVoice;
      if (s.total) out.push(s.total + ' tracked articles · ' + s.positivePct + '% positive, ' + s.negative + ' negative.');
      if (m && m.last30 != null) out.push(m.last30 + ' in the last 30 days (' + (m.deltaPct >= 0 ? '+' : '') + m.deltaPct + '% vs prior 30), net sentiment ' + (m.netSentiment >= 0 ? '+' : '') + m.netSentiment + '.');
      if (this.themes && this.themes.length) out.push('Leading theme: ' + this.themes[0].theme + ' (' + this.themes[0].count + ')' + (this.reputationWatch && this.reputationWatch.length ? ' · ' + this.reputationWatch.length + ' on reputation watch.' : ' · no negative stories flagged.'));
      if (mi && mi.estimatedValue) out.push('Est. earned-media value ~' + this.formatMoney(mi.estimatedValue) + ' · avg channel authority ' + mi.avgAuthority + '.');
      if (sov && sov.orgs && sov.orgs.length) out.push('Share of voice ' + sov.wtiShare + '% among peer NGOs (rank #' + sov.wtiRank + ' of ' + sov.orgs.length + ').');
      return out;
    },
    trendChart() {
      const t = this.trend || [];
      return {
        series: [
          { name: 'Positive', data: t.map((x) => x.positive) },
          { name: 'Neutral', data: t.map((x) => x.neutral) },
          { name: 'Negative', data: t.map((x) => x.negative) },
        ],
        options: {
          chart: { type: 'bar', stacked: true, toolbar: { show: false }, foreColor: '#8a978f', fontFamily: 'Poppins, sans-serif', animations: { speed: 500 } },
          colors: ['#2e7d32', '#94a3b8', '#c62828'],
          plotOptions: { bar: { borderRadius: 4, columnWidth: '55%', borderRadiusApplication: 'end', borderRadiusWhenStacked: 'last' } },
          dataLabels: { enabled: false },
          legend: { position: 'top', horizontalAlign: 'left', fontSize: '12px', markers: { radius: 3 }, labels: { colors: '#33403a' } },
          grid: { borderColor: '#eef2f0', strokeDashArray: 4 },
          xaxis: { categories: t.map((x) => x.label), axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: '10px' } } },
          yaxis: { labels: { style: { fontSize: '11px' } } },
          tooltip: { theme: 'light', shared: true, intersect: false },
        },
      };
    },
    corrClass() {
      const r = this.corr && this.corr.correlation;
      if (r === null || r === undefined) return 'neu';
      return r >= 0.4 ? 'pos' : r <= -0.2 ? 'neg' : 'neu';
    },
    corrChart() {
      const s = (this.corr && this.corr.series) || [];
      return {
        series: [
          { name: 'Articles', type: 'column', data: s.map((x) => x.articles) },
          { name: 'Online giving (₹)', type: 'line', data: s.map((x) => x.fundraising) },
        ],
        options: {
          chart: { type: 'line', toolbar: { show: false }, foreColor: '#8a978f', fontFamily: 'Poppins, sans-serif', animations: { speed: 500 } },
          colors: ['#9cc3a6', '#14663a'],
          stroke: { width: [0, 3], curve: 'smooth' },
          plotOptions: { bar: { borderRadius: 4, columnWidth: '45%' } },
          markers: { size: [0, 4] },
          dataLabels: { enabled: false },
          legend: { position: 'top', horizontalAlign: 'left', fontSize: '12px', markers: { radius: 3 }, labels: { colors: '#33403a' } },
          grid: { borderColor: '#eef2f0', strokeDashArray: 4 },
          xaxis: { categories: s.map((x) => x.month), axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: '11px' } } },
          yaxis: [
            { title: { text: 'Articles', style: { fontSize: '11px', fontWeight: 500 } }, labels: { style: { fontSize: '11px' } } },
            { opposite: true, title: { text: 'Online giving (₹)', style: { fontSize: '11px', fontWeight: 500 } }, labels: { style: { fontSize: '11px' }, formatter: (v) => this.formatMoney(v) } },
          ],
          tooltip: {
            theme: 'light', shared: true, intersect: false,
            y: [{ formatter: (v) => v + ' articles' }, { formatter: (v) => this.formatMoney(v) }],
          },
        },
      };
    },
  },
  mounted() {
    this.fetchNews();
    this.fetchShareOfVoice();
    this.fetchCorrelation();
  },
  methods: {
    async fetchShareOfVoice() {
      try {
        const res = await axios.get('dashboard/ceo/media-share-of-voice', { headers: this.authHeaders() });
        if (res.data && res.data.status) this.shareOfVoice = res.data;
      } catch (e) { /* panel hidden */ }
    },
    async fetchCorrelation() {
      try {
        const res = await axios.get('dashboard/ceo/media-fundraising-correlation', { headers: this.authHeaders() });
        if (res.data && res.data.status) this.corr = res.data;
      } catch (e) { /* panel hidden */ }
    },
    formatMoney(n) {
      const v = Number(n) || 0;
      if (v >= 10000000) return '₹' + (v / 10000000).toFixed(2) + ' Cr';
      if (v >= 100000) return '₹' + (v / 100000).toFixed(1) + ' L';
      if (v >= 1000) return '₹' + Math.round(v / 1000) + 'k';
      return '₹' + v;
    },
    filterBy(term) { this.searchQuery = term; this.pagination.page = 1; this.fetchNews(); window.scrollTo({ top: 0, behavior: 'smooth' }); },
    isFavicon(url) { return /s2\/favicons/.test(url || ''); },
    imgError(ev) { if (ev && ev.target) { const t = ev.target.closest('.card-thumb'); if (t) t.style.display = 'none'; else ev.target.style.display = 'none'; } },
    tierClass(tier) { return tier === 1 ? 't1' : tier === 2 ? 't2' : 't3'; },
    riskClass(v) { return v >= 40 ? 'high' : v >= 20 ? 'med' : 'low'; },
    sentColor(s) { return s === 'positive' ? '#2e7d32' : s === 'negative' ? '#c62828' : '#64748b'; },
    authHeaders() {
      const token = localStorage.getItem('token') || this.$store.state.token;
      return { 'x-auth-token': token };
    },

    async fetchNews() {
      this.loading = true;
      try {
        const res = await axios.get('dashboard/ceo/media-news', {
          params: {
            sentiment: this.selectedSentiment,
            search: this.searchQuery,
            dateFilter: this.dateFilter,
            language: this.selectedLanguage,
            page: this.pagination.page,
            limit: this.pagination.limit
          },
          headers: this.authHeaders()
        });

        if (res.data && res.data.status) {
          this.items = res.data.items || [];
          this.stats = res.data.stats || { total: 0, positive: 0, negative: 0, neutral: 0, positivePct: 0 };
          this.trend = res.data.trend || [];
          this.topPublishers = res.data.topPublishers || [];
          this.mediaImpact = res.data.mediaImpact || {};
          this.themes = res.data.themes || [];
          this.species = res.data.species || [];
          this.reputationWatch = res.data.reputationWatch || [];
          this.topWins = res.data.topWins || [];
          this.momentum = res.data.momentum || {};
          this.languageSpread = res.data.languageSpread || [];
          this.sourceSpread = res.data.sourceSpread || [];
          this.pagination = res.data.pagination || { page: 1, limit: 12, totalItems: 0, totalPages: 1 };
        }
      } catch (err) {
        console.error('Error fetching media news:', err);
        this.showSnack('Failed to load media news', '#c62828');
      } finally {
        this.loading = false;
      }
    },
    debouncedFetch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.pagination.page = 1;
        this.fetchNews();
      }, 400);
    },
    setDateFilter(val) {
      this.dateFilter = val;
      this.pagination.page = 1;
      this.fetchNews();
    },
    setSentimentFilter(val) {
      this.selectedSentiment = val;
      this.pagination.page = 1;
      this.fetchNews();
    },
    setLanguageFilter(val) {
      this.selectedLanguage = val;
      this.pagination.page = 1;
      this.fetchNews();
    },
    changePage(p) {
      this.pagination.page = p;
      this.fetchNews();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    resetFilters() {
      this.searchQuery = '';
      this.dateFilter = 'all';
      this.selectedSentiment = 'all';
      this.selectedLanguage = 'all';
      this.pagination.page = 1;
      this.fetchNews();
    },
    async triggerScraper() {
      this.scraping = true;
      try {
        const res = await axios.post('dashboard/ceo/media-news/scrape', {}, { headers: this.authHeaders() });
        if (res.data && res.data.status) {
          this.showSnack(res.data.msg || 'Media news refresh complete', '#14663a');
          this.fetchNews();
        } else {
          this.showSnack(res.data.msg || 'Failed to refresh media news', '#c62828');
        }
      } catch (err) {
        console.error('Error running scraper:', err);
        this.showSnack('Error triggering media scraper', '#c62828');
      } finally {
        this.scraping = false;
      }
    },
    async updateSentiment(item, newSentiment) {
      try {
        const res = await axios.post('dashboard/ceo/media-news/sentiment', {
          articleId: item._id,
          sentiment: newSentiment
        }, { headers: this.authHeaders() });

        if (res.data && res.data.status) {
          item.sentiment = newSentiment;
          this.showSnack(`Sentiment updated to ${newSentiment}`, '#14663a');
          this.fetchNews();
        }
      } catch (err) {
        console.error('Error updating sentiment:', err);
        this.showSnack('Failed to update sentiment', '#c62828');
      }
    },
    openAddDialog() {
      this.newArticle = { title: '', articleUrl: '', publisher: '', author: '', sentiment: 'positive', snippet: '' };
      this.addDialog = true;
    },
    async submitAddArticle() {
      if (!this.newArticle.title || !this.newArticle.articleUrl) {
        this.showSnack('Title and Article Link are required', '#c62828');
        return;
      }

      this.savingArticle = true;
      try {
        const res = await axios.post('dashboard/ceo/media-news/add', this.newArticle, { headers: this.authHeaders() });
        if (res.data && res.data.status) {
          this.showSnack('Article added successfully', '#14663a');
          this.addDialog = false;
          this.fetchNews();
        } else {
          this.showSnack(res.data.msg || 'Failed to add article', '#c62828');
        }
      } catch (err) {
        console.error('Error adding article:', err);
        this.showSnack('Error saving media article', '#c62828');
      } finally {
        this.savingArticle = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return 'Recent';
      const m = moment(dateStr);
      if (m.isSame(moment(), 'day')) return 'Today, ' + m.format('h:mm A');
      if (m.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday, ' + m.format('h:mm A');
      return m.format('MMM D, YYYY');
    },
    sentimentIcon(s) {
      if (s === 'positive') return 'mdi-emoticon-happy';
      if (s === 'negative') return 'mdi-emoticon-sad';
      return 'mdi-minus-circle';
    },
    showSnack(msg, color = '#14663a') {
      this.snackMsg = msg;
      this.snackColor = color;
      this.snackbar = true;
    }
  }
};
</script>

<style scoped>
.news-root {
  min-height: 100vh;
  background: radial-gradient(1100px 460px at 92% -8%, rgba(20,102,58,0.08), transparent 60%), linear-gradient(180deg, #f3f7f4 0%, #e9f0eb 100%);
  color: #1b2320;
  font-family: "Poppins", "Segoe UI", system-ui, sans-serif;
  padding-bottom: 48px;
}

/* Top bar */
.topbar { position: sticky; top: 0; z-index: 20; display: flex; align-items: center; justify-content: space-between;
  padding: 12px 26px; background: rgba(255,255,255,0.94); backdrop-filter: saturate(180%) blur(12px); border-bottom: 1px solid rgba(20,102,58,0.12); }
.brand { display: flex; align-items: center; gap: 12px; cursor: pointer; }
.back-btn { width: 38px; height: 38px; border-radius: 11px; border: 1px solid #cfe5d8; background: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; }
.brand-logo { width: 46px; height: 46px; border-radius: 12px; background: linear-gradient(135deg,#14663a,#2e7d32); display: flex; align-items: center; justify-content: center; overflow: hidden; }
.brand-logo img { width: 34px; height: 34px; object-fit: contain; }
.brand-title { font-size: 18px; font-weight: 800; color: #16321f; letter-spacing: 0.2px; }
.brand-sub { font-size: 12px; color: #7d8f85; margin-top: 1px; }
.topbar-actions { display: flex; align-items: center; gap: 10px; }
.btn { display: inline-flex; align-items: center; gap: 6px; font-family: inherit; font-size: 13px; font-weight: 600; border-radius: 999px; padding: 8px 15px; cursor: pointer; transition: background .14s ease, transform .1s ease; border: 1px solid transparent; }
.btn.ghost { background: #fff; color: #14663a; border-color: #cfe5d8; }
.btn.ghost:hover:not(:disabled) { background: #eef6f0; }
.btn.solid { background: #14663a; color: #fff; }
.btn.solid:hover:not(:disabled) { background: #124f2d; }
.btn:disabled { opacity: 0.6; cursor: default; }
.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.loader { display: flex; flex-direction: column; align-items: center; gap: 14px; padding: 100px 0; color: #7d8f85; font-size: 14px; }
.body { max-width: 1280px; margin: 0 auto; padding: 24px 26px 0; }

/* Auto summary banner */
.summary-banner { display: flex; align-items: flex-start; gap: 10px; background: #fff; border: 1px solid #e8efea; border-left: 4px solid #14663a; border-radius: 14px; padding: 14px 18px; margin-bottom: 18px; box-shadow: 0 6px 22px rgba(20,60,40,0.05); }
.sb-lines { display: flex; flex-wrap: wrap; gap: 6px 16px; }
.sb-lines span { font-size: 13px; color: #33403a; font-weight: 500; position: relative; }
.sb-lines span:not(:last-child)::after { content: ""; }

/* Summary */
.sum-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 18px; }
.sum-card { background: #fff; border: 1px solid #e8efea; border-radius: 16px; padding: 18px 20px; box-shadow: 0 6px 22px rgba(20,60,40,0.05); }
.sum-head { display: flex; align-items: center; gap: 7px; font-size: 12.5px; font-weight: 700; color: #5a6b62; }
.sum-val { font-size: 32px; font-weight: 800; color: #16321f; line-height: 1.1; margin-top: 8px; }
.sum-val.pos { color: #2e7d32; } .sum-val.neg { color: #c62828; } .sum-val.neu { color: #475569; }
.sum-pct { font-size: 15px; font-weight: 600; color: #8a978f; }
.sum-sub { font-size: 11.5px; color: #9aa8a1; margin-top: 3px; }

/* Impact */
.impact { background: #fff; border: 1px solid #e8efea; border-radius: 16px; padding: 16px 20px; margin-bottom: 18px; box-shadow: 0 6px 22px rgba(20,60,40,0.05); }
.impact-head { display: flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 700; color: #16321f; margin-bottom: 13px; }
.muted { font-size: 11.5px; font-weight: 500; color: #9aa8a1; }
.impact-tiles { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; }
.imp.emv { background: #eef6f0; border-color: #cfe5d8; }
.imp.emv .imp-v { color: #14663a; }
.imp { background: #f7faf8; border: 1px solid #eef2f0; border-radius: 12px; padding: 13px 15px; }
.imp-v { font-size: 24px; font-weight: 800; color: #14663a; line-height: 1; }
.imp-l { font-size: 10.5px; color: #8a978f; font-weight: 600; margin-top: 5px; }
.imp.risk.low .imp-v { color: #2e7d32; }
.imp.risk.med .imp-v { color: #e08600; }
.imp.risk.high { background: #fdf3f3; border-color: #f3d6d6; }
.imp.risk.high .imp-v { color: #c62828; }
@media (max-width: 760px) { .impact-tiles, .sum-grid { grid-template-columns: repeat(2, 1fr); } }

/* Coverage reach: languages + sources */
.reach { display: flex; flex-wrap: wrap; gap: 22px; align-items: flex-start; background: #fff; border: 1px solid #e8efea; border-radius: 16px; padding: 14px 18px; margin-bottom: 20px; box-shadow: 0 6px 22px rgba(20,60,40,0.05); }
.reach-block { display: flex; flex-direction: column; gap: 8px; min-width: 220px; }
.reach-label { display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 700; color: #16321f; text-transform: uppercase; letter-spacing: 0.3px; }
.reach-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.reach-chip { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 600; color: #33403a; background: #f2f7f3; border: 1px solid #e3ede6; border-radius: 20px; padding: 4px 11px; }
.reach-chip b { color: #14663a; font-weight: 800; }
.reach-chip.prime { background: #eaf4ee; border-color: #cfe6d7; }
.reach-chip.src { background: #f6f6fb; border-color: #e7e7f2; }
.reach-chip.src b { color: #4b4b8f; }

/* Media ↔ Fundraising correlation */
.corr-panel { margin-bottom: 20px; }
.corr-lead { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 12px; padding: 9px 12px; border-radius: 10px; margin-bottom: 12px; background: #f7faf8; border: 1px solid #eef2f0; }
.corr-lead .corr-stat b { font-size: 17px; font-weight: 800; color: #16321f; }
.corr-lead .corr-word { font-size: 12.5px; font-weight: 700; text-transform: capitalize; padding: 2px 9px; border-radius: 20px; background: #eef2f0; color: #5a6b62; }
.corr-lead.pos .corr-word { background: #eaf4ee; color: #2e7d32; }
.corr-lead.neg .corr-word { background: #fdf3f3; color: #c62828; }
.corr-lead .corr-note { font-size: 11px; color: #8a978f; flex: 1 1 240px; }

/* Insight row */
.insight-row { display: grid; grid-template-columns: 1fr 340px; gap: 16px; margin-bottom: 20px; }
.panel { background: #fff; border: 1px solid #e8efea; border-radius: 16px; padding: 18px 20px; box-shadow: 0 6px 22px rgba(20,60,40,0.05); }
.panel-title { display: flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 700; color: #16321f; margin-bottom: 10px; }
.pub-list { display: flex; flex-direction: column; gap: 7px; }
.pub-row { display: flex; align-items: center; gap: 10px; padding: 8px 10px; background: #f7faf8; border: 1px solid #eef2f0; border-radius: 10px; }
.pub-count { flex: 0 0 32px; text-align: center; font-size: 14px; font-weight: 800; color: #14663a; background: #eaf4ee; border-radius: 7px; padding: 4px 0; }
.pub-name { flex: 1; font-size: 13px; color: #33403a; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pub-row.clickable { cursor: pointer; transition: background .12s ease; }
.pub-row.clickable:hover { background: #eef6f0; }
.pub-sent { display: flex; gap: 5px; }
.pub-head { display: flex; align-items: center; gap: 10px; padding: 0 10px 8px; font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; color: #a7b3ac; }
.pub-head .ph-count { flex: 0 0 32px; text-align: center; }
.pub-head .ph-name { flex: 1; }
.pub-head .ph-da, .pub-head .ph-sent { flex: 0 0 auto; }
.chip-pos, .chip-neg { display: inline-flex; align-items: center; gap: 2px; }
/* Share of Voice */
.sov-panel { margin-bottom: 20px; }
.sov-list { display: flex; flex-direction: column; gap: 9px; }
.sov-row { display: flex; align-items: center; gap: 11px; }
.sov-row.self .sov-name { color: #14663a; font-weight: 800; }
.sov-name { flex: 0 0 150px; font-size: 12.5px; color: #33403a; font-weight: 600; display: flex; align-items: center; gap: 6px; }
.sov-you { font-size: 9px; font-weight: 800; color: #fff; background: #14663a; border-radius: 4px; padding: 1px 5px; text-transform: uppercase; }
.sov-track { flex: 1; height: 11px; background: #eef2f0; border-radius: 6px; overflow: hidden; }
.sov-fill { display: block; height: 100%; border-radius: 6px; background: #94a3b8; transition: width .5s ease; }
.sov-fill.me { background: linear-gradient(90deg, #14663a, #2e7d32); }
.sov-count { flex: 0 0 78px; text-align: right; font-size: 13px; font-weight: 800; color: #33403a; }
.sov-count em { font-style: normal; font-weight: 600; color: #8a978f; font-size: 11.5px; }
.sp-chip { cursor: pointer; font-family: inherit; }
.sp-chip:hover { background: #e2efe7; border-color: #cfe5d8; }
@media (max-width: 1100px) { .impact-tiles { grid-template-columns: repeat(3, 1fr); } }
.chip-pos, .chip-neg { font-size: 11px; font-weight: 800; border-radius: 6px; padding: 2px 7px; }
.chip-pos { color: #2e7d32; background: #eaf4ee; } .chip-neg { color: #c62828; background: #fdecec; }
.insight-row.wide { grid-template-columns: 1fr 1fr; }
@media (max-width: 900px) { .insight-row, .insight-row.wide { grid-template-columns: 1fr; } }

/* Momentum */
.impact-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.momentum { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 700; padding: 4px 11px; border-radius: 999px; }
.momentum.up { color: #2e7d32; background: #eaf4ee; }
.momentum.down { color: #c62828; background: #fdecec; }

/* Themes */
.theme-list { display: flex; flex-direction: column; gap: 9px; }
.theme-row { display: flex; align-items: center; gap: 10px; }
.theme-name { flex: 0 0 168px; font-size: 12.5px; color: #33403a; font-weight: 600; }
.theme-track { flex: 1; height: 9px; background: #eef2f0; border-radius: 6px; overflow: hidden; }
.theme-fill { display: block; height: 100%; border-radius: 6px; }
.theme-count { flex: 0 0 32px; text-align: right; font-size: 13px; font-weight: 800; color: #33403a; }
.species-chips { display: flex; align-items: center; flex-wrap: wrap; gap: 7px; margin-top: 14px; padding-top: 12px; border-top: 1px solid #f0f4f1; }
.sp-label { font-size: 11px; font-weight: 700; color: #8a978f; text-transform: uppercase; letter-spacing: 0.3px; }
.sp-chip { font-size: 12px; color: #33403a; background: #f2f6f3; border: 1px solid #e6ece8; border-radius: 999px; padding: 3px 10px; }
.sp-chip b { color: #14663a; }

/* Reputation watch / wins */
.panel-title.sub { font-size: 12px; margin-top: 16px; padding-top: 12px; border-top: 1px solid #f0f4f1; }
.watch-list { display: flex; flex-direction: column; gap: 7px; }
.watch-row { display: flex; align-items: center; gap: 9px; padding: 8px 10px; border-radius: 10px; text-decoration: none; border: 1px solid #eef2f0; transition: background .12s ease; }
.watch-row.neg { background: #fdf5f4; border-color: #f3ddd8; }
.watch-row.pos { background: #f2f8f4; border-color: #dcebe1; }
.watch-row:hover { filter: brightness(0.985); }
.watch-da { flex: 0 0 auto; font-size: 11px; font-weight: 800; border-radius: 6px; padding: 2px 7px; }
.watch-da.t1 { color: #14663a; background: #e5f2ea; } .watch-da.t2 { color: #1565c0; background: #e9f1fb; } .watch-da.t3 { color: #64748b; background: #eef2f0; }
.watch-title { flex: 1; font-size: 12.5px; color: #23302a; font-weight: 600; line-height: 1.35; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.watch-pub { flex: 0 0 auto; font-size: 11px; color: #8a978f; font-weight: 600; max-width: 90px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.watch-empty { display: flex; align-items: center; gap: 7px; font-size: 13px; color: #2e7d32; font-weight: 600; padding: 14px 4px; }

/* Authority badge */
.da-badge { flex: 0 0 auto; font-size: 11px; font-weight: 800; border-radius: 6px; padding: 2px 7px; }
.da-badge.sm { font-size: 10px; padding: 1px 6px; margin-left: 6px; }
.da-badge.t1 { color: #14663a; background: #e5f2ea; }
.da-badge.t2 { color: #1565c0; background: #e9f1fb; }
.da-badge.t3 { color: #64748b; background: #eef2f0; }

/* Filters */
.filters { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin-bottom: 14px; }
.search { display: flex; align-items: center; gap: 8px; background: #fff; border: 1px solid #dcebe1; border-radius: 999px; padding: 8px 14px; min-width: 300px; flex: 1; }
.search input { border: none; outline: none; background: transparent; font-family: inherit; font-size: 13.5px; color: #23302a; width: 100%; }
.search input::placeholder { color: #a7b3ac; }
.clear { border: none; background: transparent; cursor: pointer; display: flex; }
.chips { display: flex; gap: 6px; flex-wrap: wrap; }
.chip { font-family: inherit; font-size: 12.5px; font-weight: 600; color: #5a6b62; background: #fff; border: 1px solid #dcebe1; border-radius: 999px; padding: 6px 13px; cursor: pointer; transition: all .14s ease; }
.chip:hover { background: #f2f8f4; }
.chip.on { background: #14663a; border-color: #14663a; color: #fff; }
.chip.pos.on { background: #2e7d32; border-color: #2e7d32; }
.chip.neg.on { background: #c62828; border-color: #c62828; }
.chip.neu.on { background: #64748b; border-color: #64748b; }
.chip.lang b { font-weight: 800; color: #14663a; margin-left: 2px; }
.chip.lang.on b { color: #cfe6d7; }

.result-note { font-size: 12px; color: #8a978f; font-weight: 600; margin-bottom: 12px; }

/* Article grid */
.grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(360px, 1fr)); gap: 18px; }
.card { background: #fff; border: 1px solid #e8efea; border-left: 4px solid #cbd5d0; border-radius: 16px; padding: 18px 20px; display: flex; flex-direction: column; box-shadow: 0 6px 22px rgba(20,60,40,0.05); transition: transform .12s ease, box-shadow .12s ease; }
.card:hover { transform: translateY(-2px); box-shadow: 0 12px 30px rgba(20,60,40,0.1); }
.card.positive { border-left-color: #2e7d32; }
.card.negative { border-left-color: #c62828; }
.card.neutral { border-left-color: #94a3b8; }
.card-thumb { height: 150px; border-radius: 11px; overflow: hidden; margin-bottom: 14px; background: #f2f6f3; border: 1px solid #eef2f0; display: flex; align-items: center; justify-content: center; }
.card-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.card-thumb.fav { height: 64px; }
.card-thumb.fav img { width: 38px; height: 38px; object-fit: contain; }
.card-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 10px; }
.pub { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 700; color: #14663a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.badge { display: inline-flex; align-items: center; gap: 4px; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.3px; padding: 3px 8px; border-radius: 999px; flex: 0 0 auto; }
.badge.positive { color: #2e7d32; background: #eaf4ee; }
.badge.negative { color: #c62828; background: #fdecec; }
.badge.neutral { color: #64748b; background: #eef2f0; }
.card-title { font-size: 15px; font-weight: 700; color: #16321f; line-height: 1.45; text-decoration: none; display: block; margin-bottom: 8px; }
.card-title:hover { color: #14663a; text-decoration: underline; }
.card-snippet { font-size: 13px; color: #5a6b62; line-height: 1.55; margin: 0 0 14px; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.card-foot { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: auto; padding-top: 12px; border-top: 1px solid #f0f4f1; }
.date { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; color: #8a978f; font-weight: 500; }
.foot-actions { display: flex; align-items: center; gap: 8px; }
.mini-btn { display: inline-flex; align-items: center; gap: 4px; font-family: inherit; font-size: 11.5px; font-weight: 600; color: #64748b; background: #f7faf8; border: 1px solid #e6ece8; border-radius: 8px; padding: 4px 9px; cursor: pointer; }
.mini-btn:hover { background: #eef4ef; color: #14663a; }
.read { display: inline-flex; align-items: center; gap: 3px; font-size: 12px; font-weight: 700; color: #14663a; text-decoration: none; }
.read:hover { text-decoration: underline; }

.empty { text-align: center; padding: 70px 0; color: #8a978f; display: flex; flex-direction: column; align-items: center; gap: 8px; }
.empty h3 { color: #33403a; font-size: 17px; margin: 6px 0 0; }
.empty p { font-size: 13px; margin: 0 0 12px; }

.pager { display: flex; align-items: center; justify-content: center; gap: 16px; margin-top: 26px; font-size: 13px; color: #5a6b62; font-weight: 600; }

/* Add dialog */
.add-card { border-radius: 16px !important; padding: 4px; }
.add-title { display: flex; align-items: center; justify-content: space-between; font-size: 17px; font-weight: 700; color: #16321f; padding: 18px 20px 12px; border-bottom: 1px solid #eef2f0; }
.add-body { padding: 18px 20px; }
.add-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 12px; }
.add-actions { display: flex; justify-content: flex-end; gap: 10px; padding: 4px 20px 18px; }
</style>
